import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAnalytics from '../hooks/useAnalytics';

interface InquiryModalProps {
    isOpen: boolean;
    onClose: () => void;
    product: any;
    firmId: number;
}

function InquiryModal({ isOpen, onClose, product, firmId }: InquiryModalProps) {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await api.post('/public/inquiries', {
                firm_id: firmId,
                product_id: product.id,
                inquiry: formData
            });
            if (response.success) {
                setSuccess(true);
                setTimeout(() => {
                    onClose();
                    setSuccess(false);
                    setFormData({ name: '', email: '', phone: '', message: '' });
                }, 2000);
            }
        } catch (error) {
            console.error('Inquiry failed:', error);
            alert('Failed to send inquiry. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-2xl font-black text-slate-900">Request Quote</h3>
                        <p className="text-sm text-slate-500 mt-1">We'll get back to you soon</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {success ? (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h4 className="text-xl font-bold text-slate-900 mb-2">Inquiry Sent!</h4>
                        <p className="text-slate-600">The seller will contact you shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Your Name *</label>
                            <input
                                required
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                placeholder="John Doe"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Email (Optional)</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                placeholder="you@example.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Phone Number *</label>
                            <div className="flex">
                                <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-slate-300 bg-slate-50 text-slate-600 text-sm font-semibold">
                                    +91
                                </span>
                                <input
                                    required
                                    type="tel"
                                    pattern="[0-9]{10}"
                                    maxLength={10}
                                    value={formData.phone}
                                    onChange={e => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                                    className="flex-1 px-4 py-3 border border-slate-300 rounded-r-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                    placeholder="9876543210"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Message</label>
                            <textarea
                                rows={4}
                                value={formData.message}
                                onChange={e => setFormData({ ...formData, message: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none transition-all"
                                placeholder="I'm interested in this product..."
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Sending...' : 'Send Inquiry'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

interface EMICalculatorProps {
    price: number;
}

function EMICalculator({ price }: EMICalculatorProps) {
    const [downPayment, setDownPayment] = useState(price * 0.2);
    const [tenure, setTenure] = useState(36); // months
    const [interestRate, setInterestRate] = useState(9.5); // %

    const loanAmount = price - downPayment;
    const r = interestRate / 12 / 100;
    const emi = loanAmount > 0
        ? Math.round((loanAmount * r * Math.pow(1 + r, tenure)) / (Math.pow(1 + r, tenure) - 1))
        : 0;

    return (
        <div className="bg-white rounded-2xl border border-indigo-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
                <div className="bg-indigo-600 rounded-lg p-1.5 text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                </div>
                <h3 className="text-xl font-black text-slate-900">EMI Calculator</h3>
            </div>

            <div className="space-y-6">
                <div>
                    <div className="flex justify-between mb-2">
                        <label className="text-sm font-bold text-slate-600">Down Payment (₹)</label>
                        <span className="text-sm font-black text-indigo-600">₹{parseFloat(downPayment.toString()).toLocaleString()}</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max={price * 0.8}
                        step="1000"
                        value={downPayment}
                        onChange={(e) => setDownPayment(parseFloat(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-2">Tenure (Months)</label>
                        <select
                            value={tenure}
                            onChange={(e) => setTenure(parseInt(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                        >
                            <option value="12">12 Months</option>
                            <option value="24">24 Months</option>
                            <option value="36">36 Months</option>
                            <option value="48">48 Months</option>
                            <option value="60">60 Months</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-600 mb-2">Int. Rate (%)</label>
                        <input
                            type="number"
                            value={interestRate}
                            onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                        />
                    </div>
                </div>

                <div className="bg-indigo-600 rounded-2xl p-6 text-center text-white shadow-lg shadow-indigo-200">
                    <p className="text-xs font-bold opacity-80 uppercase tracking-widest mb-1">Estimated Monthly EMI</p>
                    <p className="text-3xl font-black">₹{emi.toLocaleString()}</p>
                </div>
            </div>
        </div>
    );
}

function StoreProductPage() {
    const { slug, id } = useParams<{ slug: string; id: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<any>(null);
    const [firm, setFirm] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);

    const { logEvent } = useAnalytics(firm?.id, id);

    useEffect(() => {
        loadProduct();
    }, [slug, id]);

    const loadProduct = async () => {
        try {
            setLoading(true);
            const firmResponse = await api.get(`/public/store/firm/${slug}`);

            if (!firmResponse.success) {
                navigate(`/store/firm/${slug}`);
                return;
            }

            const firmData = firmResponse.data.firm;
            const productData = firmResponse.data.products?.find((p: any) => String(p.id) === String(id));

            if (!productData) {
                navigate(`/store/firm/${slug}`);
                return;
            }

            setFirm(firmData);
            setProduct(productData);
        } catch (err) {
            console.error('Error loading product:', err);
            navigate(`/store/firm/${slug}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-slate-600 font-medium">Loading product...</p>
                </div>
            </div>
        );
    }

    if (!product || !firm) {
        return null;
    }

    const images = product.images && product.images.length > 0 ? product.images : [null];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
            {/* Navigation */}
            <nav className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <Link
                            to={`/store/firm/${slug}`}
                            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold transition-colors group"
                        >
                            <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Store
                        </Link>
                        <h1 className="text-lg font-bold text-slate-900">{firm.name}</h1>
                    </div>
                </div>
            </nav>

            {/* Product Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Image Gallery */}
                    <div className="space-y-4">
                        <div className="aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 shadow-xl">
                            {images[selectedImage] ? (
                                <img
                                    src={images[selectedImage]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-300">
                                    <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            )}
                        </div>

                        {/* Thumbnail Strip */}
                        {images.length > 1 && (
                            <div className="flex gap-3 overflow-x-auto pb-2">
                                {images.map((img: string | null, idx: number) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedImage(idx)}
                                        className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${selectedImage === idx
                                            ? 'border-indigo-600 shadow-lg scale-110'
                                            : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                    >
                                        {img ? (
                                            <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                                                <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="space-y-6">
                        {/* Category Badge */}
                        {product.category && (
                            <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold uppercase tracking-wider">
                                {product.category}
                            </div>
                        )}

                        {/* Title */}
                        <div>
                            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4">{product.name}</h1>
                            <div className="flex items-baseline gap-4">
                                <span className="text-4xl font-black text-indigo-600">
                                    ₹{parseFloat(product.selling_price || '0').toLocaleString()}
                                </span>
                                {product.in_stock ? (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-bold rounded-full">
                                        In Stock
                                    </span>
                                ) : (
                                    <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-bold rounded-full">
                                        Out of Stock
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        {product.description && (
                            <div className="bg-slate-50 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-slate-900 mb-3">Description</h3>
                                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{product.description}</p>
                            </div>
                        )}

                        {/* Product Details & Industry Fields */}
                        <div className="bg-slate-50 rounded-2xl p-6 space-y-6">
                            <div>
                                <h3 className="text-xl font-black text-slate-900 mb-4">Product Details</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {product.sku && (
                                        <div className="bg-white p-3 rounded-xl border border-slate-200">
                                            <span className="block text-xs font-bold text-slate-400 uppercase">SKU</span>
                                            <span className="text-slate-900 font-bold">{product.sku}</span>
                                        </div>
                                    )}
                                    {product.brand && (
                                        <div className="bg-white p-3 rounded-xl border border-slate-200">
                                            <span className="block text-xs font-bold text-slate-400 uppercase">Brand</span>
                                            <span className="text-slate-900 font-bold">{product.brand}</span>
                                        </div>
                                    )}
                                    {product.color && firm.industry !== 'vehicle' && (
                                        <div className="bg-white p-3 rounded-xl border border-slate-200">
                                            <span className="block text-xs font-bold text-slate-400 uppercase">Color</span>
                                            <span className="text-slate-900 font-bold">{product.color}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Industry Specific Fields */}
                            {firm.industry && (
                                <div className="border-t border-slate-200 pt-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-black text-slate-900 capitalize">
                                            {(firm.industry || '').replace(/_/g, ' ')} Info
                                        </h3>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {firm.industry === 'vehicle' && (
                                            <>
                                                {(product.custom_fields?.year || product.year) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Year</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields?.year || product.year}</span>
                                                    </div>
                                                )}
                                                {(product.custom_fields?.fuel_type || product.fuel_type) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Fuel Type</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields?.fuel_type || product.fuel_type}</span>
                                                    </div>
                                                )}
                                                {(product.custom_fields?.mileage || product.mileage) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Mileage</span>
                                                        <span className="text-slate-900 font-bold">{parseFloat(product.custom_fields?.mileage || product.mileage || '0').toLocaleString()} km</span>
                                                    </div>
                                                )}
                                                {(product.custom_fields?.transmission || product.transmission) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Transmission</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields?.transmission || product.transmission}</span>
                                                    </div>
                                                )}
                                                {(product.custom_fields?.rto_registration || product.rto_registration) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">RTO Registration</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields?.rto_registration || product.rto_registration}</span>
                                                    </div>
                                                )}
                                                {(product.custom_fields?.insurance_valid_until || product.insurance_valid_until) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Insurance Valid</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields?.insurance_valid_until || product.insurance_valid_until}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {firm.industry === 'electronics' && (
                                            <>
                                                {(product.custom_fields?.model_number || product.model_number) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Model</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields?.model_number || product.model_number}</span>
                                                    </div>
                                                )}
                                                {(product.custom_fields?.warranty_months || product.warranty_months) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Warranty</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields?.warranty_months || product.warranty_months} Months</span>
                                                    </div>
                                                )}
                                                {(product.custom_fields?.installation_required || product.installation_required) && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Installation</span>
                                                        <span className="text-slate-900 font-bold">{(product.custom_fields?.installation_required || product.installation_required) === 'true' ? 'Included' : 'Not Included'}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {firm.industry === 'shoes' && (
                                            <>
                                                {product.custom_fields?.brand && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Brand</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields.brand}</span>
                                                    </div>
                                                )}
                                                <div className="bg-white p-3 rounded-xl border border-slate-200 col-span-full">
                                                    <span className="block text-xs font-bold text-slate-400 uppercase mb-2">Sizes Available</span>
                                                    <div className="flex gap-4">
                                                        {product.custom_fields?.size_uk && (
                                                            <div className="text-center">
                                                                <span className="block text-[10px] text-slate-400 font-bold">UK</span>
                                                                <span className="px-3 py-1 bg-slate-100 rounded-lg font-black text-slate-800">{product.custom_fields.size_uk}</span>
                                                            </div>
                                                        )}
                                                        {product.custom_fields?.size_eu && (
                                                            <div className="text-center">
                                                                <span className="block text-[10px] text-slate-400 font-bold">EU</span>
                                                                <span className="px-3 py-1 bg-slate-100 rounded-lg font-black text-slate-800">{product.custom_fields.size_eu}</span>
                                                            </div>
                                                        )}
                                                        {product.custom_fields?.size_us && (
                                                            <div className="text-center">
                                                                <span className="block text-[10px] text-slate-400 font-bold">US</span>
                                                                <span className="px-3 py-1 bg-slate-100 rounded-lg font-black text-slate-800">{product.custom_fields.size_us}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {product.custom_fields?.outer_material && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Material</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields.outer_material}</span>
                                                    </div>
                                                )}
                                                {product.custom_fields?.sole_material && (
                                                    <div className="bg-white p-3 rounded-xl border border-slate-200">
                                                        <span className="block text-xs font-bold text-slate-400 uppercase">Sole</span>
                                                        <span className="text-slate-900 font-bold">{product.custom_fields.sole_material}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Generic Custom Fields */}
                                        {Object.entries(product.custom_fields || {}).map(([key, value]) => {
                                            const handledKeys = [
                                                'year', 'fuel_type', 'mileage', 'transmission', 'rto_registration', 'insurance_valid_until',
                                                'model_number', 'warranty_months', 'installation_required', 'brand', 'color',
                                                'size_uk', 'size_eu', 'size_us', 'outer_material', 'sole_material', 'closure'
                                            ];
                                            if (handledKeys.includes(key) || !value) return null;

                                            return (
                                                <div key={key} className="bg-white p-3 rounded-xl border border-slate-200">
                                                    <span className="block text-xs font-bold text-slate-400 uppercase">{key.replace(/_/g, ' ')}</span>
                                                    <span className="text-slate-900 font-bold">{String(value)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* EMI Calculator for Vehicle */}
                            {firm.industry === 'vehicle' && product.selling_price > 0 && (
                                <EMICalculator price={parseFloat(product.selling_price)} />
                            )}
                        </div>

                        {/* CTA Buttons */}
                        <div className="space-y-3 pt-4">
                            <button
                                onClick={() => {
                                    logEvent('click', { element: 'inquiry_button' });
                                    setModalOpen(true);
                                }}
                                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-5 rounded-2xl font-black text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-2xl transform hover:scale-[1.02]"
                            >
                                Request Quote
                            </button>

                            {firm.whatsapp_number && (
                                <a
                                    href={`https://wa.me/${firm.whatsapp_number}?text=Hi, I'm interested in ${product.name}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => logEvent('click', { element: 'whatsapp_product_button' })}
                                    className="flex items-center justify-center gap-3 w-full bg-green-500 text-white py-5 rounded-2xl font-bold text-lg hover:bg-green-600 transition-all shadow-lg hover:shadow-xl"
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                    Chat on WhatsApp
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </main >

            {/* Footer */}
            < footer className="bg-slate-900 text-white mt-16" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
                    <p className="text-slate-400">© {new Date().getFullYear()} {firm.name}. All rights reserved.</p>
                </div>
            </footer >

            {/* Inquiry Modal */}
            < InquiryModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)
                }
                product={product}
                firmId={firm.id}
            />
        </div >
    );
}

export default StoreProductPage;
